import websocketService from '../../services/websocketService';
import {
  setConnectionStatus,
  addRealtimeEvent,
  addActiveUser,
  removeActiveUser,
} from '../slices/realtimeSlice';
import {
  addNotification,
  showToast
} from '../slices/notificationsSlice';
import {
  addEvent,
} from '../slices/eventsSlice';
import {
  processRealTimeUpdate,
  addRealTimeUpdate,
} from '../slices/sharingSlice';

let registeredHandlers = {};
let registeredConnectionStatusCallback = null;

function teardownWebSocketEventHandlers() {
  Object.entries(registeredHandlers).forEach(([eventType, handler]) => {
    websocketService.off(eventType, handler);
  });
  registeredHandlers = {};
}

export const realtimeMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  const state = store.getState();

  if (action.type === 'auth/loginSuccess') {
    const token = action.payload.token;
    websocketService.connect(token);

    setupWebSocketEventHandlers(store);

    if (registeredConnectionStatusCallback) {
      websocketService.offConnectionStatusChange(
        registeredConnectionStatusCallback
      );
    }

    const connectionStatusCallback = (status, details) => {
      store.dispatch(setConnectionStatus({ status, details }));

      if (status === 'connected') {
        store.dispatch(showToast({
          type: 'success',
          message: 'Real-time updates connected',
          duration: 3000
        }));
      } else if (status === 'disconnected') {
        store.dispatch(showToast({
          type: 'warning',
          message: 'Real-time updates disconnected',
          duration: 5000
        }));
      } else if (status === 'reconnected') {
        store.dispatch(showToast({
          type: 'success',
          message: 'Real-time updates reconnected',
          duration: 3000
        }));
      } else if (status === 'error') {
        store.dispatch(showToast({
          type: 'error',
          message: `Connection error: ${details}`,
          duration: 7000
        }));
      }
    };

    registeredConnectionStatusCallback = connectionStatusCallback;
    websocketService.onConnectionStatusChange(connectionStatusCallback);
  }

  // Disconnect WebSocket when user logs out
  if (action.type === 'auth/logout') {
    websocketService.disconnect();
    store.dispatch(setConnectionStatus({ status: 'disconnected' }));
  }

  return result;
};

function setupWebSocketEventHandlers(store) {
  teardownWebSocketEventHandlers();

  const childEventCreatedHandler = (data) => {
    const eventData = data.data;

    store.dispatch(addEvent(eventData));
    store.dispatch(addRealtimeEvent({
      type: 'child_event_created',
      data: eventData,
      timestamp: data.timestamp || new Date().toISOString()
    }));

    store.dispatch(addNotification({
      id: `event_${eventData.id}_${Date.now()}`,
      type: 'info',
      title: 'New Event Added',
      message: (
        `New ${eventData.event_type} event recorded for ` +
        `${eventData.child_name}`
      ),
      timestamp: new Date().toISOString(),
      childId: eventData.child_id,
      eventId: eventData.id
    }));

    store.dispatch(showToast({
      type: 'info',
      message: (
        `New ${eventData.event_type} event recorded for ` +
        `${eventData.child_name}`
      ),
      duration: 5000
    }));
  };

  const milestoneCompletedHandler = (data) => {
    const milestoneData = data.data;

    store.dispatch(addRealtimeEvent({
      type: 'milestone_completed',
      data: milestoneData,
      timestamp: data.timestamp || new Date().toISOString()
    }));

    store.dispatch(addNotification({
      id: `milestone_${milestoneData.id}_${Date.now()}`,
      type: 'success',
      title: 'Milestone Achieved!',
      message: (
        `${milestoneData.child_name} completed: ` +
        `${milestoneData.milestone_title}`
      ),
      timestamp: new Date().toISOString(),
      childId: milestoneData.child_id,
      milestoneId: milestoneData.id
    }));

    store.dispatch(showToast({
      type: 'success',
      message: `${milestoneData.child_name} completed a milestone!`,
      duration: 7000
    }));
  };

  const healthAlertHandler = (data) => {
    const alertData = data.data;

    store.dispatch(addRealtimeEvent({
      type: 'health_alert',
      data: alertData,
      timestamp: data.timestamp || new Date().toISOString()
    }));

    store.dispatch(addNotification({
      id: `health_alert_${alertData.id || Date.now()}_${Date.now()}`,
      type: 'warning',
      title: 'Health Alert',
      message: alertData.alert_message,
      timestamp: new Date().toISOString(),
      childId: alertData.child_id,
      priority: 'high'
    }));

    store.dispatch(showToast({
      type: 'warning',
      message: `Health Alert: ${alertData.alert_message}`,
      duration: 10000
    }));
  };

  const growthUpdatedHandler = (data) => {
    const growthData = data.data;

    store.dispatch(addRealtimeEvent({
      type: 'growth_updated',
      data: growthData,
      timestamp: data.timestamp || new Date().toISOString()
    }));

    store.dispatch(addNotification({
      id: `growth_${growthData.id}_${Date.now()}`,
      type: 'info',
      title: 'Growth Updated',
      message: (
        `New growth measurements recorded for ${growthData.child_name}`
      ),
      timestamp: new Date().toISOString(),
      childId: growthData.child_id
    }));

    store.dispatch(showToast({
      type: 'info',
      message: `Growth measurements updated for ${growthData.child_name}`,
      duration: 5000
    }));
  };

  const userJoinedHandler = (data) => {
    store.dispatch(addRealtimeEvent({
      type: 'user_joined',
      data: data,
      timestamp: new Date().toISOString()
    }));

    store.dispatch(showToast({
      type: 'info',
      message: `${data.user_name} is now online`,
      duration: 3000
    }));
  };

  // sharing_update: an invitation was sent, accepted, declined, or access was revoked.
  // The server wraps the sharing payload under data.data, matching the same nesting
  // used by child_event_created.
  const sharingUpdateHandler = (data) => {
    const sharingData = data.data;
    const eventType = sharingData.event_type;

    // Map server event_type names to the keys understood by processRealTimeUpdate.
    const eventTypeToUpdateType = {
      invitation_sent: 'invitation_sent',
      invitation_accepted: 'invitation_accepted',
      invitation_declined: 'invitation_declined',
      access_revoked: 'share_revoked',
    };

    const updateType = eventTypeToUpdateType[eventType];

    if (updateType) {
      store.dispatch(processRealTimeUpdate({ type: updateType, data: sharingData }));
    }

    store.dispatch(addRealtimeEvent({
      type: 'sharing_update',
      data: sharingData,
      timestamp: data.timestamp || new Date().toISOString(),
    }));
  };

  // invitation_update: the current user received a new invitation.
  const invitationUpdateHandler = (data) => {
    const invitationData = data.data;

    store.dispatch(processRealTimeUpdate({
      type: 'invitation_received',
      data: invitationData,
    }));

    store.dispatch(addRealtimeEvent({
      type: 'invitation_update',
      data: invitationData,
      timestamp: data.timestamp || new Date().toISOString(),
    }));

    store.dispatch(addNotification({
      id: `invitation_${invitationData.share_id || Date.now()}_${Date.now()}`,
      type: 'info',
      title: 'New Invitation',
      message: `You have been invited to access ${invitationData.child_name}`,
      timestamp: new Date().toISOString(),
      childId: data.child_id,
    }));

    store.dispatch(showToast({
      type: 'info',
      message: `You have been invited to access ${invitationData.child_name}`,
      duration: 7000,
    }));
  };

  // permission_update: an existing share's permission level was changed.
  const permissionUpdateHandler = (data) => {
    const permissionData = data.data;

    store.dispatch(processRealTimeUpdate({
      type: 'permission_updated',
      data: permissionData,
    }));

    store.dispatch(addRealtimeEvent({
      type: 'permission_update',
      data: permissionData,
      timestamp: data.timestamp || new Date().toISOString(),
    }));
  };

  // user_activity: a co-caregiver became active or changed what they are doing.
  // Record in the realtime slice only -- no persistent notification needed.
  const userActivityHandler = (data) => {
    const activityData = data.data || data;
    const activityUser = activityData.user;
    const activityType = activityData.activity_type;

    if (activityUser) {
      if (activityType === 'offline') {
        store.dispatch(removeActiveUser(activityUser.id));
      } else {
        store.dispatch(addActiveUser({
          ...activityUser,
          lastActivity: activityType,
          childId: activityData.child_id,
        }));
      }
    }

    store.dispatch(addRealtimeEvent({
      type: 'user_activity',
      data: activityData,
      timestamp: data.timestamp || new Date().toISOString(),
    }));
  };

  // share_notification: a generic sharing notification pushed from the server.
  const shareNotificationHandler = (data) => {
    const notificationData = data.data;

    store.dispatch(addRealTimeUpdate(notificationData));

    store.dispatch(addRealtimeEvent({
      type: 'share_notification',
      data: notificationData,
      timestamp: data.timestamp || new Date().toISOString(),
    }));

    store.dispatch(addNotification({
      id: `share_notification_${Date.now()}`,
      type: notificationData.notification_type === 'access_revoked' ? 'warning' : 'info',
      title: notificationData.title || 'Sharing Update',
      message: notificationData.message || '',
      timestamp: new Date().toISOString(),
      childId: data.child_id,
    }));

    store.dispatch(showToast({
      type: notificationData.notification_type === 'access_revoked' ? 'warning' : 'info',
      message: notificationData.message || notificationData.title || 'Sharing update received',
      duration: notificationData.notification_type === 'access_revoked' ? 10000 : 5000,
    }));
  };

  registeredHandlers = {
    child_event_created: childEventCreatedHandler,
    milestone_completed: milestoneCompletedHandler,
    health_alert: healthAlertHandler,
    growth_updated: growthUpdatedHandler,
    user_joined: userJoinedHandler,
    sharing_update: sharingUpdateHandler,
    invitation_update: invitationUpdateHandler,
    permission_update: permissionUpdateHandler,
    user_activity: userActivityHandler,
    share_notification: shareNotificationHandler,
  };

  Object.entries(registeredHandlers).forEach(([eventType, handler]) => {
    websocketService.on(eventType, handler);
  });
}